import { TradingPlansTable } from '../../components/tables/Tables';
import { useEffect, useState } from 'react';
import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';
import { PageSpinner } from '../../components/spinner/Spinner';
import NothingToSeeHere from '../../components/nothing-to-see-here/NothingToSeeHere';
import { config } from '../../Constants';
import { Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const TradingPlans = () => {
  const [data, setData] = useState([]);
  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const baseURL = config.baseURL;
  const [opened, { open, close }] = useDisclosure(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    minAmount: '',
    maxAmount: '',
    duration: '',
    expectedProfit: '',
    features: '',
    isActive: true,
    order: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${baseURL}/trading-plans/admin`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setLoading(false);
        setData(data.plans);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        minAmount: Number(formData.minAmount),
        maxAmount: formData.maxAmount ? Number(formData.maxAmount) : null,
        duration: Number(formData.duration),
        expectedProfit: Number(formData.expectedProfit),
        order: Number(formData.order),
        features: formData.features.split('\n').filter((f) => f.trim() !== ''),
      };

      await axios.post(`${baseURL}/trading-plans/admin`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success('Trading plan created successfully');
      close();
      setFormData({
        title: '',
        subtitle: '',
        minAmount: '',
        maxAmount: '',
        duration: '',
        expectedProfit: '',
        features: '',
        isActive: true,
        order: 0,
      });
      navigate(0);
    } catch (error) {
      toast.error(error.response?.data?.msg || 'Failed to create trading plan');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className='content trading-plans-page'>
      <div className='title'>
        <h1 className='title'>Trading Plans</h1>
        <button className='btn-primary' onClick={open}>
          Add New Plan
        </button>
      </div>
      {loading ? (
        <PageSpinner />
      ) : !loading && data.length > 0 ? (
        <TradingPlansTable data={data} />
      ) : (
        <NothingToSeeHere />
      )}

      <Modal
        opened={opened}
        onClose={close}
        centered
        size='lg'
        title='Create New Trading Plan'
        overlayProps={{
          backgroundOpacity: 0.55,
        }}
      >
        <form onSubmit={handleSubmit} className='trading-plan-form'>
          <div className='form-group'>
            <label>Title</label>
            <input
              type='text'
              name='title'
              value={formData.title}
              onChange={handleChange}
              placeholder='e.g. Silver Plan'
              required
            />
          </div>

          <div className='form-group'>
            <label>Subtitle</label>
            <input
              type='text'
              name='subtitle'
              value={formData.subtitle}
              onChange={handleChange}
              placeholder='e.g. Essential Trading'
              required
            />
          </div>

          <div className='form-row'>
            <div className='form-group'>
              <label>Min Amount ($)</label>
              <input
                type='number'
                name='minAmount'
                value={formData.minAmount}
                onChange={handleChange}
                placeholder='1000'
                required
              />
            </div>

            <div className='form-group'>
              <label>Max Amount ($) - Optional</label>
              <input
                type='number'
                name='maxAmount'
                value={formData.maxAmount}
                onChange={handleChange}
                placeholder='10000'
              />
            </div>
          </div>

          <div className='form-row'>
            <div className='form-group'>
              <label>Duration (days)</label>
              <input
                type='number'
                name='duration'
                value={formData.duration}
                onChange={handleChange}
                placeholder='30'
                required
              />
            </div>

            <div className='form-group'>
              <label>Expected Profit (%)</label>
              <input
                type='number'
                name='expectedProfit'
                value={formData.expectedProfit}
                onChange={handleChange}
                placeholder='15'
                required
              />
            </div>
          </div>

          <div className='form-group'>
            <label>Features (one per line)</label>
            <textarea
              name='features'
              value={formData.features}
              onChange={handleChange}
              placeholder='Access to basic trading algorithms.&#10;Portfolio tracking and analytics.&#10;Email support.'
              rows={5}
              required
            />
          </div>

          <div className='form-row'>
            <div className='form-group'>
              <label>Display Order</label>
              <input
                type='number'
                name='order'
                value={formData.order}
                onChange={handleChange}
                placeholder='0'
              />
            </div>

            <div className='form-group checkbox'>
              <label>
                <input
                  type='checkbox'
                  name='isActive'
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                Active (visible to users)
              </label>
            </div>
          </div>

          <button type='submit' className='btn-primary' disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Plan'}
          </button>
        </form>
      </Modal>
    </main>
  );
};

export default TradingPlans;
